import { useCallback, useEffect } from 'react';
import type { ListViewIssue } from '../../../services/get-list-view-issues/types';
import { useHierarchyStore } from '../../hierarchy';
import { useBulkSelectionStore } from '../bulk-selection-store';

/*
    If the user selects a group that is collapsed, we should expand the group
    and select all the issues once are fetched, these hooks help us to do this.
*/
export const useSelectCollapsedGroups = () => {
	const [{ expandedItems, childrenItems }, { setExpandedItems }] = useHierarchyStore();
	const [{ fetchingGroups, selectedGroups }, { selectIssues, selectGroup, setFetchingGroups }] =
		useBulkSelectionStore();

	useEffect(() => {
		// if the fetch of the group issues is done and the group is selected
		// we should select the group issues
		if (fetchingGroups.length) {
			let groupFetchDone: string[] = [];
			fetchingGroups.forEach((groupId) => {
				const group = childrenItems[groupId];
				if (group?.loading === false && selectedGroups.has(groupId)) {
					selectIssues({ issues: group.data.map((i) => String(i.issueId)) });
					groupFetchDone = [...groupFetchDone, groupId];
				}
			});
			if (groupFetchDone.length) {
				setFetchingGroups(fetchingGroups.filter((g) => !groupFetchDone.includes(g)));
			}
		}
	}, [childrenItems, fetchingGroups, selectIssues, selectedGroups, setFetchingGroups]);

	const selectCollapsedGroups = useCallback(
		(groups: ListViewIssue[]) => {
			let groupToFetch: string[] = [];
			groups.forEach((group) => {
				const { issueId, childSummary } = group;
				const hasChildren = childSummary?.hasChildren;

				selectGroup(issueId);
				const hasIssuesToFetch =
					expandedItems[issueId] === undefined &&
					childrenItems[issueId] === undefined &&
					hasChildren;

				setExpandedItems(issueId, true);
				// if the group is selected and we have not fetched children of the group
				// we should expand the group and fetch them
				if (hasIssuesToFetch) {
					groupToFetch = [...groupToFetch, issueId];
				}
			});
			setFetchingGroups(groupToFetch);
		},
		[childrenItems, expandedItems, selectGroup, setExpandedItems, setFetchingGroups],
	);

	return { selectCollapsedGroups, fetchingGroups: fetchingGroups.length > 0 };
};
